import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Badge, Button, Form, Spinner, Table, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import Layout from "app/universal/layout/Layout";
import LoadingOverlay from "app/orderTool/components/LoadingOverlay";
import HelpTip from "app/orderTool/components/HelpTip";
import DocumentsButton from "app/orderTool/components/DocumentsButton";
import AlphaBanner from "app/orderTool/components/AlphaBanner";
import { moneyCellStyle, stickyTable, EmptyState } from "app/orderTool/components/pageUi";
import ReglesCategorisation from "./ReglesCategorisation";
import {
  getBankOperations,
  changeBankCategorie,
  rapprocherBankOperation,
  searchFactures,
  lierBankOperation,
  exportBankFlux,
  annulerTraitementBankOperations,
  validerCategorieBankOperations,
  devaliderCategorieBankOperations,
} from "state/banque/actions";

// Date locale au format YYYY-MM-DD (évite le décalage UTC de toISOString).
const localYmd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Les 3 boîtes du workflow.
const PHASE_DEFAUT = "RECEPTION";
const PHASE_META = {
  RECEPTION: { label: "Boîte de réception", color: "danger" },
  CATEGORISE: { label: "Flux catégorisés", color: "primary" },
  TRAITE: { label: "Flux traités", color: "dark" },
};

const FAMILLE_COLOR = {
  TRAITEUR: "success", CANTINE: "primary", TR: "info", LIVRAISON: "info",
  ESPECE: "secondary", CHEQUE: "secondary", AUTRE: "secondary",
  FOURNISSEUR: "warning", A_FLECHER: "danger",
};

const CONFIANCE_COLOR = { VERT: "success", ORANGE: "warning", ROUGE: "danger" };
const CONFIANCE_LABEL = { VERT: "Fiable", ORANGE: "À vérifier", ROUGE: "À vérifier" };

const euros = (cents) =>
  cents != null ? (cents / 100).toFixed(2).replace(".", ",") + " €" : "—";

/**
 * Rapprochement bancaire (export Delubac) — internalisé dans le BO.
 * Workflow en 3 boîtes : Réception (non catégorisé / à valider) → Catégorisés
 * (catégorie validée) → Traités (rapproché ou exporté). Catégorisation pilotée
 * par des règles administrables. Backend : /api/v2/admin/bank.
 */
const RapprochementBancaire = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = "Rapprochement bancaire — Le Moulin";
    return () => { document.title = prev; };
  }, []);

  const dispatch = useDispatch();
  const { import: importInfo, stats, phases, familles, categories, operations, sheets, isLoading, isInitialized } =
    useSelector((state) => state.banque);
  const isProd = process.env.REACT_APP_ENV === "prod";

  const [screen, setScreen] = useState("flux"); // 'flux' | 'regles'
  const [viewMode, setViewMode] = useState("table"); // 'table' | 'pipeline'
  const [phase, setPhase] = useState(PHASE_DEFAUT);
  const [famille, setFamille] = useState(null); // sous-filtre famille (phase CATEGORISE)
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rapprochId, setRapprochId] = useState(null);
  const [rapprochDate, setRapprochDate] = useState("");
  const [lierId, setLierId] = useState(null);
  const [lierQuery, setLierQuery] = useState("");
  const [lierResults, setLierResults] = useState([]);
  const [lierLoading, setLierLoading] = useState(false);
  const [lierSearched, setLierSearched] = useState(false);
  const [dateMin, setDateMin] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return localYmd(d);
  });
  const [dateMax, setDateMax] = useState(() => localYmd(new Date()));
  const [visibleCount, setVisibleCount] = useState(50);
  const PAGE = 50;
  const fileRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkCategorie, setBulkCategorie] = useState("");

  // En mode pipeline on charge les 3 phases d'un coup (phase=ALL).
  const isPipeline = viewMode === "pipeline";
  const showRapproch = phase !== "RECEPTION";

  const reload = useCallback(() => {
    if (isPipeline) {
      dispatch(getBankOperations({ phase: "ALL", search }));
    } else {
      dispatch(getBankOperations({ phase, famille: phase === "CATEGORISE" ? famille : null, search }));
    }
  }, [dispatch, isPipeline, phase, famille, search]);

  useEffect(() => { reload(); }, [reload]);

  // Un flux est annulable s'il a été exporté ou rattaché manuellement (sans paiement).
  const isAnnulable = (op) => !!(op.exporteSuivi || (op.statut === "RAPPROCHEE" && op.rapprocheManuellement));
  // Lignes sélectionnables selon la phase.
  const isSelectable = (op) => {
    if (op.phase === "TRAITE") return isAnnulable(op);
    return true; // réception + catégorisés
  };

  const dateOk = (o) =>
    (!dateMin || (o.dateOperation && o.dateOperation >= dateMin)) &&
    (!dateMax || (o.dateOperation && o.dateOperation <= dateMax));
  const filteredOps = (operations || []).filter(dateOk);

  useEffect(() => {
    setVisibleCount(PAGE);
    setSelectedIds([]);
    setBulkCategorie("");
  }, [phase, famille, search, dateMin, dateMax, viewMode]);

  const selectableIds = filteredOps.filter(isSelectable).map((o) => o.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleUpload = async () => {
    const file = fileRef.current && fileRef.current.files[0];
    if (!file) { toast.error("Choisissez un fichier export bancaire (.xlsx)"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("fichier", file);
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const apiKey = process.env.REACT_APP_LEMOULIN_API_KEY;
      const token = window.localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/api/v2/admin/bank/import.json?apiKey=${apiKey}`, {
        method: "POST",
        headers: { "X-Custom-Authorization": `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        toast.error(data.message || "Import impossible");
      } else {
        toast.success(data.message || "Import effectué");
        if (fileRef.current) fileRef.current.value = "";
        reload();
      }
    } catch (e) {
      toast.error("Erreur réseau lors de l'import");
    } finally {
      setUploading(false);
    }
  };

  const handleCategorie = async (id, categorie) => {
    setBusy(true);
    const resp = await dispatch(changeBankCategorie(id, categorie));
    setBusy(false);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Changement de catégorie refusé");
      return;
    }
    reload();
  };

  const handleValider = async (ids, categorie = null) => {
    if (!ids.length) return;
    setBusy(true);
    const resp = await dispatch(validerCategorieBankOperations(ids, categorie));
    setBusy(false);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Validation impossible");
      return;
    }
    toast.success(resp?.message || "Catégorie validée");
    setSelectedIds([]);
    setBulkCategorie("");
    reload();
  };

  const handleDevalider = async (ids) => {
    if (!ids.length) return;
    setBusy(true);
    const resp = await dispatch(devaliderCategorieBankOperations(ids));
    setBusy(false);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Action impossible");
      return;
    }
    toast.success(resp?.message || "Renvoyé en réception");
    setSelectedIds([]);
    reload();
  };

  const handleAnnuler = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(`Annuler le traitement de ${ids.length} flux ? Ils repartiront dans leur famille et pourront être ré-exportés.`)) return;
    setBusy(true);
    const resp = await dispatch(annulerTraitementBankOperations(ids));
    setBusy(false);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Annulation impossible");
      return;
    }
    toast.success(resp?.message || "Traitement annulé");
    setSelectedIds([]);
    reload();
  };

  const openLier = (op) => { setLierId(op.id); setLierQuery(""); setLierResults([]); setLierSearched(false); };

  const doSearchFactures = async (op) => {
    if (!lierQuery || lierQuery.trim().length < 2) return;
    setLierLoading(true);
    const resp = await dispatch(searchFactures(lierQuery.trim()));
    setLierLoading(false);
    const list = resp?.result?.result || resp?.result || [];
    setLierResults(Array.isArray(list) ? list : []);
    setLierSearched(true);
  };

  const handleLier = async (op, facture) => {
    setBusy(true);
    const resp = await dispatch(lierBankOperation(op.id, {
      type: facture.type, commandeId: facture.id, numbering: facture.numbering || facture.invoiceNumber || null,
    }));
    setBusy(false);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Rattachement impossible");
      return;
    }
    toast.success("Crédit rattaché à la facture");
    setLierId(null); setLierResults([]);
    reload();
  };

  const handleRapprocher = async (op) => {
    setBusy(true);
    const resp = await dispatch(rapprocherBankOperation(op.id, { commandeId: null, paidDate: rapprochDate || null }));
    setBusy(false);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Rapprochement impossible");
      return;
    }
    toast.success(resp?.message || "Rapprochement validé — facture payée");
    setRapprochId(null); setRapprochDate("");
    reload();
  };

  const handleExport = async () => {
    setBusy(true);
    const resp = await dispatch(exportBankFlux());
    setBusy(false);
    const body = resp?.result || {};
    if (resp?.error || body.success === false) {
      toast.error(body.message || resp?.message || "Export impossible");
      return;
    }
    toast.success(body.message || "Flux exportés vers le Google Sheet");
    reload();
  };

  // Téléchargement direct d'un .xlsx des flux catégorisés sur la période.
  const handleExportPeriode = async () => {
    setExporting(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const apiKey = process.env.REACT_APP_LEMOULIN_API_KEY;
      const token = window.localStorage.getItem("token");
      const params = new URLSearchParams({ apiKey: apiKey || "" });
      if (dateMin) params.set("from", dateMin);
      if (dateMax) params.set("to", dateMax);
      const res = await fetch(`${baseUrl}/api/v2/admin/bank/export-flux-excel.json?${params.toString()}`, {
        method: "GET",
        headers: { "X-Custom-Authorization": `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error("Export Excel impossible");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `flux-categorises-${dateMin || ""}_${dateMax || ""}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Erreur réseau lors de l'export Excel");
    } finally {
      setExporting(false);
    }
  };

  const controleKo = importInfo && importInfo.statutControle && importInfo.statutControle !== "OK";

  // ── Cellule « catégorie » (éditable en réception, badge ailleurs) ──
  const renderCategorieCell = (op) => {
    if (op.phase === "RECEPTION") {
      return (
        <Form.Control
          as="select"
          size="sm"
          value={op.categorie || ""}
          onChange={(e) => handleCategorie(op.id, e.target.value)}
          disabled={busy}
          style={{ minWidth: 180 }}
        >
          <option value="">— Non catégorisé —</option>
          {(categories || []).map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </Form.Control>
      );
    }
    return (
      <div>
        <Badge variant="light" className="border">{op.categorieLabel}</Badge>
        {op.exporteSuivi && <div className="mt-1"><Badge variant="success">✓ exporté</Badge></div>}
        {op.phase === "TRAITE" && op.statut === "RAPPROCHEE" && !op.rapprocheManuellement && !op.exporteSuivi && (
          <div className="mt-1 text-muted" style={{ fontSize: "0.75rem" }}>
            <Badge variant="light" className="border">Paiement — non annulable ici</Badge>
          </div>
        )}
      </div>
    );
  };

  // ── Cellule « proposition » (phases catégorisés/traités) ──
  const renderPropositionCell = (op) => {
    const docs = op.documents && op.documents.length > 0 ? (
      <div className="mb-1"><DocumentsButton documents={op.documents} stopPropagation /></div>
    ) : null;
    if (op.statut === "RAPPROCHEE") {
      return <>{docs}<Badge variant="success">
        Rapproché{op.proposition?.referenceFacture ? ` · ${op.proposition.referenceFacture}` : (op.commandeRapprocheeNumbering ? ` · ${op.commandeRapprocheeNumbering}` : "")}
      </Badge></>;
    }
    return <>{docs}{renderPropositionInner(op)}</>;
  };

  const renderPropositionInner = (op) => {
    if (op.statut === "RAPPROCHEE") {
      return (
        <Badge variant="success">
          Rapproché{op.proposition?.referenceFacture ? ` · ${op.proposition.referenceFacture}` : (op.commandeRapprocheeNumbering ? ` · ${op.commandeRapprocheeNumbering}` : "")}
        </Badge>
      );
    }
    if (!op.proposition) return <span className="text-muted">—</span>;
    const isCantine = op.typeRapprochement === "vosfactures_invoice";
    const payBloque = isCantine && !isProd;
    return (
      <div>
        <div>
          <Badge variant={CONFIANCE_COLOR[op.proposition.confiance] || "secondary"} className="mr-1">
            {CONFIANCE_LABEL[op.proposition.confiance] || op.proposition.confiance}
          </Badge>
          {isCantine && <Badge variant="dark" className="mr-1">F/C cantine</Badge>}
          {op.proposition.referenceFacture || op.proposition.commandeNumbering}
          {op.proposition.clientLibelle ? ` — ${op.proposition.clientLibelle}` : ""}
          {op.proposition.montantFactureTtcCents != null && (
            <span className="text-muted"> (TTC facture : {euros(op.proposition.montantFactureTtcCents)})</span>
          )}
        </div>
        {rapprochId === op.id ? (
          <div className="d-flex align-items-center mt-1" style={{ gap: 6 }}>
            <Form.Control type="date" size="sm" style={{ width: 150 }} value={rapprochDate}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setRapprochDate(e.target.value)} disabled={busy} />
            <Button size="sm" variant="success" disabled={busy || !rapprochDate || payBloque} onClick={() => handleRapprocher(op)}>
              {isCantine ? "Payer la facture F/C" : "Valider le paiement"}
            </Button>
            <Button size="sm" variant="link" className="p-0" onClick={() => { setRapprochId(null); setRapprochDate(""); }}>Annuler</Button>
          </div>
        ) : payBloque ? (
          <div className="mt-1 text-muted" style={{ fontSize: "0.8rem" }}>
            <Badge variant="light" className="border">Paiement F/C en prod uniquement</Badge>
          </div>
        ) : (
          <Button size="sm" variant="outline-success" className="mt-1"
            onClick={() => { setRapprochId(op.id); setRapprochDate(op.dateOperation || ""); }}>
            {isCantine ? "Payer la facture F/C" : "Payer / Rapprocher"}
          </Button>
        )}
      </div>
    );
  };

  // ── Cellule « facture rattachée » (recherche/lier) ──
  const renderFactureCell = (op) => {
    if (op.commandeRapprocheeId) {
      return (
        <div>
          <Badge variant={op.rapprocheManuellement ? "secondary" : "success"} className="mr-1">
            {op.rapprocheManuellement ? "Traité" : "Payée"}
          </Badge>
          {op.commandeRapprocheeNumbering || `#${op.commandeRapprocheeId}`}
        </div>
      );
    }
    if (lierId === op.id) {
      return (
        <div>
          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <Form.Control size="sm" style={{ width: 150 }} placeholder="N° facture, client…" value={lierQuery}
              onChange={(e) => { setLierQuery(e.target.value); setLierSearched(false); }}
              onKeyDown={(e) => e.key === "Enter" && doSearchFactures(op)} disabled={lierLoading} />
            <Button size="sm" variant="outline-primary" disabled={lierLoading} onClick={() => doSearchFactures(op)}>
              {lierLoading ? "…" : "Chercher"}
            </Button>
            <Button size="sm" variant="link" className="p-0" onClick={() => { setLierId(null); setLierResults([]); }}>Annuler</Button>
          </div>
          {lierResults.length > 0 && (
            <div className="mt-1" style={{ maxHeight: 160, overflowY: "auto" }}>
              {lierResults.map((f) => (
                <div key={`${f.type}-${f.id}`} className="d-flex align-items-center justify-content-between border-bottom py-1" style={{ gap: 6 }}>
                  <span style={{ fontSize: "0.8rem" }}>
                    <Badge variant={f.type === "vosfactures_invoice" ? "dark" : "light"} className="border mr-1">
                      {f.type === "vosfactures_invoice" ? "F/C" : "F/B"}
                    </Badge>
                    {f.invoiceNumber || f.numbering}
                    {f.clientLibelle ? ` — ${f.clientLibelle}` : ""}
                    {f.montantTtcCents != null ? ` · ${euros(f.montantTtcCents)}` : ""}
                    {f.paid && <Badge variant="success" className="ml-1">payée</Badge>}
                  </span>
                  <Button size="sm" variant="outline-success" disabled={busy} onClick={() => handleLier(op, f)}>Lier</Button>
                </div>
              ))}
            </div>
          )}
          {!lierLoading && lierSearched && lierResults.length === 0 && (
            <div className="text-muted mt-1" style={{ fontSize: "0.78rem" }}>Aucune facture trouvée.</div>
          )}
        </div>
      );
    }
    return <Button size="sm" variant="outline-secondary" onClick={() => openLier(op)}>Rechercher / lier</Button>;
  };

  const renderCheckbox = (op) => (
    isSelectable(op) ? (
      <Form.Check type="checkbox" checked={selectedIds.includes(op.id)} disabled={busy}
        onChange={(e) => setSelectedIds((prev) => e.target.checked ? [...prev, op.id] : prev.filter((x) => x !== op.id))} />
    ) : null
  );

  const nbCols = 1 + 3 + 1 + (showRapproch ? 2 : 0); // checkbox+date+libellé+montant+catégorie(+prop+facture)

  // ── Rendu d'une carte (mode pipeline) ──
  const renderCard = (op) => (
    <div key={op.id} className="border rounded p-2 mb-2 bg-white">
      <div className="d-flex justify-content-between">
        <small className="text-muted">{op.dateOperation ? new Date(op.dateOperation).toLocaleDateString("fr-FR") : "—"}</small>
        <strong>{euros(op.montantCents)}</strong>
      </div>
      <div style={{ fontSize: "0.82rem" }}>{op.libelle}</div>
      <div className="mt-1">
        {op.phase === "RECEPTION" ? (
          <Form.Control as="select" size="sm" value={op.categorie || ""} disabled={busy}
            onChange={(e) => handleCategorie(op.id, e.target.value)}>
            <option value="">— Non catégorisé —</option>
            {(categories || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </Form.Control>
        ) : (
          <>
            <Badge variant="light" className="border">{op.categorieLabel}</Badge>
            {op.exporteSuivi && <Badge variant="success" className="ml-1">exporté</Badge>}
            {op.statut === "RAPPROCHEE" && <Badge variant="success" className="ml-1">rapproché</Badge>}
          </>
        )}
      </div>
      <div className="mt-1 d-flex" style={{ gap: 6 }}>
        {op.phase === "RECEPTION" && (
          <Button size="sm" variant="outline-primary" className="py-0 px-1" style={{ fontSize: "0.75rem" }}
            disabled={busy || !op.categorie} onClick={() => handleValider([op.id])}>Valider</Button>
        )}
        {op.phase === "CATEGORISE" && (
          <Button size="sm" variant="outline-secondary" className="py-0 px-1" style={{ fontSize: "0.75rem" }}
            disabled={busy} onClick={() => handleDevalider([op.id])}>↩ Réception</Button>
        )}
        {op.phase === "TRAITE" && isAnnulable(op) && (
          <Button size="sm" variant="outline-danger" className="py-0 px-1" style={{ fontSize: "0.75rem" }}
            disabled={busy} onClick={() => handleAnnuler([op.id])}>Annuler</Button>
        )}
      </div>
    </div>
  );

  const renderPipeline = () => {
    const cols = ["RECEPTION", "CATEGORISE", "TRAITE"];
    return (
      <div className="d-flex" style={{ gap: 12, alignItems: "flex-start" }}>
        {cols.map((p) => {
          const colOps = filteredOps.filter((o) => o.phase === p);
          const validablesIds = p === "RECEPTION" ? colOps.filter((o) => o.categorie).map((o) => o.id) : [];
          return (
            <div key={p} style={{ flex: 1, minWidth: 0 }}>
              <div className={`mb-2 p-2 rounded text-white bg-${PHASE_META[p].color}`}>
                <div className="d-flex justify-content-between align-items-center">
                  <span><strong>{PHASE_META[p].label}</strong> <Badge variant="light">{colOps.length}</Badge></span>
                  {p === "RECEPTION" && (
                    <Button size="sm" variant="light" className="py-0 px-2" style={{ fontSize: "0.78rem" }}
                      disabled={busy || validablesIds.length === 0}
                      onClick={() => handleValider(validablesIds)}
                      title="Valider tous les flux de la réception qui ont une catégorie">
                      ✓ Valider tout ({validablesIds.length})
                    </Button>
                  )}
                </div>
              </div>
              <div style={{ maxHeight: "65vh", overflowY: "auto", background: "#f5f5f5", padding: 6, borderRadius: 4 }}>
                {colOps.length === 0 ? (
                  <div className="text-muted text-center py-3" style={{ fontSize: "0.8rem" }}>Aucun flux</div>
                ) : colOps.slice(0, 200).map(renderCard)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (screen === "regles") {
    return (
      <Layout>
        <div style={{ padding: "15px" }}>
          <AlphaBanner feature="Rapprochement bancaire" />
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">
              Règles de catégorisation
              <HelpTip title="Comment fonctionnent les règles ?">
                <p className="mb-1">À l'import, chaque flux au crédit est confronté aux règles <strong>par priorité croissante</strong> (1, 2, 3…). La <strong>première règle qui matche</strong> attribue sa catégorie — les suivantes sont ignorées. Mettez donc les règles les plus spécifiques avec une <strong>petite priorité</strong>.</p>
                <p className="mb-1"><strong>Motif</strong> : du texte recherché dans le flux. Deux modes :</p>
                <ul className="mb-1 pl-3">
                  <li><strong>Contient</strong> (case « Regex » décochée) : le motif doit apparaître tel quel. Ex : <code>DELIVEROO</code>.</li>
                  <li><strong>Expression régulière</strong> (case « Regex » cochée) : motif avancé, insensible à la casse. Le <code>|</code> = « ou ». Ex : <code>PLUXEE|EDENRED|SWILE</code> ; <code>\b</code> = limite de mot.</li>
                </ul>
                <p className="mb-1"><strong>Champ cible</strong> : où chercher — le libellé, l'émetteur, ou les deux.</p>
                <p className="mb-1"><strong>Catégorie</strong> : celle qui sera <em>proposée</em>. ⚠️ Ce n'est qu'une proposition : le flux reste en <strong>boîte de réception</strong> tant qu'un humain ne l'a pas <strong>validé</strong>.</p>
                <p className="mb-1">Un flux qui ne matche <strong>aucune</strong> règle reste <strong>non catégorisé</strong> (à traiter à la main, ou créez une règle).</p>
                <p className="mb-0"><strong>↻ Ré-appliquer</strong> : relance les règles sur les flux <strong>encore en réception</strong> uniquement (les flux validés ou traités ne sont jamais modifiés). Utile après avoir ajouté/modifié une règle.</p>
              </HelpTip>
            </h4>
            <Button size="sm" variant="outline-secondary" onClick={() => { setScreen("flux"); reload(); }}>← Retour aux flux</Button>
          </div>
          <ReglesCategorisation />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ padding: "15px" }}>
        <AlphaBanner feature="Rapprochement bancaire" />
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
          <h4 className="mb-0">
            Rapprochement bancaire
            <HelpTip title="Rapprochement bancaire">
              <p className="mb-1">Importez l'<strong>export Delubac</strong> (.xlsx) : les <strong>crédits</strong> sont dédoublonnés puis catégorisés par des <strong>règles</strong> administrables.</p>
              <p className="mb-1">Workflow : <strong>Boîte de réception</strong> (à catégoriser / valider) → <strong>Flux catégorisés</strong> (catégorie validée, rapprochement facture) → <strong>Flux traités</strong> (exporté ou rapproché).</p>
              <p className="mb-0">L'import peut être refait plusieurs fois par jour sans doublon.</p>
            </HelpTip>
          </h4>
          <div className="d-flex align-items-center">
            <Button size="sm" variant="outline-dark" className="mr-2" onClick={() => setScreen("regles")}>⚙ Règles</Button>
            <Form.Control ref={fileRef} type="file" accept=".xlsx,.xls" size="sm" style={{ width: 240 }} className="mr-2" disabled={uploading} />
            <Button variant="success" size="sm" onClick={handleUpload} disabled={uploading}>
              {uploading ? <><Spinner animation="border" size="sm" className="mr-1" /> Import…</> : "Importer le relevé"}
            </Button>
          </div>
        </div>

        {importInfo && (
          <Alert variant={controleKo ? "warning" : "light"} className="py-2">
            <div>
              <strong>Compte {importInfo.numeroCompte}</strong>
              {importInfo.periodeDebut ? ` · période ${importInfo.periodeDebut} → ${importInfo.periodeFin}` : ""}
              {" · "}
              <Badge variant={controleKo ? "warning" : "success"}>{importInfo.statutControle}</Badge>
            </div>
            {controleKo && importInfo.messageControle && (
              <div style={{ whiteSpace: "pre-wrap", fontSize: "0.82rem" }} className="mt-1">{importInfo.messageControle}</div>
            )}
          </Alert>
        )}

        {/* Boîtes (phases) + switch de présentation */}
        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap" style={{ gap: 8 }}>
          <div className="d-flex flex-wrap" style={{ gap: 6 }}>
            {(phases || []).map((p) => (
              <Button key={p.key} size="sm"
                variant={phase === p.key ? (PHASE_META[p.key]?.color || "primary") : "outline-secondary"}
                onClick={() => setPhase(p.key)} disabled={isPipeline}>
                {p.label} <Badge variant="light">{p.nb}</Badge> · {euros(p.totalCents)}
              </Button>
            ))}
          </div>
          <div className="btn-group btn-group-sm" role="group">
            <Button variant={viewMode === "table" ? "dark" : "outline-dark"} onClick={() => setViewMode("table")}>Tableau</Button>
            <Button variant={viewMode === "pipeline" ? "dark" : "outline-dark"} onClick={() => setViewMode("pipeline")}>Pipeline</Button>
          </div>
        </div>

        {/* Sous-filtre famille : seulement phase Catégorisés en mode tableau */}
        {!isPipeline && phase === "CATEGORISE" && (
          <div className="d-flex flex-wrap mb-2" style={{ gap: 6 }}>
            <Button size="sm" variant={famille === null ? "primary" : "outline-secondary"} onClick={() => setFamille(null)}>
              Toutes
            </Button>
            {(familles || []).filter((f) => f.nb > 0).map((f) => (
              <Button key={f.key} size="sm"
                variant={famille === f.key ? FAMILLE_COLOR[f.key] || "primary" : "outline-secondary"}
                onClick={() => setFamille(f.key)}>
                {f.label} <Badge variant="light">{f.nb}</Badge> · {euros(f.totalCents)}
              </Button>
            ))}
          </div>
        )}

        {/* Toolbar : recherche, dates, exports */}
        <div className="d-flex justify-content-end align-items-center mb-2 flex-wrap" style={{ gap: 6 }}>
          <Form.Control size="sm" style={{ width: 200 }} placeholder="Rechercher (libellé, émetteur…)"
            value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          <Form.Control type="date" size="sm" style={{ width: 140 }} title="Du" value={dateMin} onChange={(e) => setDateMin(e.target.value)} />
          <Form.Control type="date" size="sm" style={{ width: 140 }} title="Au" value={dateMax} onChange={(e) => setDateMax(e.target.value)} />
          <Button size="sm" variant="outline-success" onClick={handleExportPeriode} disabled={exporting}>
            {exporting ? "Excel…" : "⬇ Excel (période)"}
          </Button>
          <Button size="sm" variant="outline-info" onClick={handleExport} disabled={busy}>Exporter (Google Sheet)</Button>
          {sheets?.flux && (
            <a href={sheets.flux} target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>↗ Sheet</a>
          )}
        </div>

        {/* Barre d'action groupée (selon la phase) */}
        {!isPipeline && selectedIds.length > 0 && (
          <div className="d-flex align-items-center mb-2 flex-wrap" style={{ gap: 10 }}>
            <span><strong>{selectedIds.length}</strong> ligne(s) sélectionnée(s)</span>
            {phase === "RECEPTION" && (
              <>
                <Form.Control as="select" size="sm" style={{ width: 220 }} value={bulkCategorie}
                  onChange={(e) => setBulkCategorie(e.target.value)} disabled={busy}>
                  <option value="">Garder la catégorie proposée</option>
                  {(categories || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </Form.Control>
                <Button size="sm" variant="success" disabled={busy} onClick={() => handleValider(selectedIds, bulkCategorie || null)}>
                  Valider la catégorie
                </Button>
              </>
            )}
            {phase === "CATEGORISE" && (
              <Button size="sm" variant="warning" disabled={busy} onClick={() => handleDevalider(selectedIds)}>
                ↩ Renvoyer en réception
              </Button>
            )}
            {phase === "TRAITE" && (
              <Button size="sm" variant="danger" disabled={busy} onClick={() => handleAnnuler(selectedIds)}>
                Annuler le traitement
              </Button>
            )}
            <Button size="sm" variant="link" className="p-0" onClick={() => setSelectedIds([])}>Tout désélectionner</Button>
          </div>
        )}

        <div style={{ position: "relative" }}>
          <LoadingOverlay show={(isLoading && !isInitialized) || busy} message="Chargement…" minHeight={200} />
          {isInitialized && operations.length === 0 && !importInfo ? (
            <EmptyState icon="🏦" title="Aucun relevé importé"
              hint="Importez l'export Delubac (.xlsx) pour détecter les crédits, les catégoriser et proposer les rapprochements." />
          ) : isPipeline ? (
            renderPipeline()
          ) : (
            <Table size="sm" hover striped responsive className={stickyTable}>
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <Form.Check type="checkbox" checked={allSelected} disabled={selectableIds.length === 0 || busy}
                      onChange={(e) => setSelectedIds(e.target.checked ? selectableIds : [])} title="Tout sélectionner" />
                  </th>
                  <th style={{ whiteSpace: "nowrap" }}>Date</th>
                  <th>Libellé</th>
                  <th className="text-right">Montant</th>
                  <th>Catégorie</th>
                  {showRapproch && <th>Proposition</th>}
                  {showRapproch && <th>Facture rattachée</th>}
                </tr>
              </thead>
              <tbody>
                {filteredOps.slice(0, visibleCount).map((op) => (
                  <tr key={op.id}>
                    <td>{renderCheckbox(op)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{op.dateOperation ? new Date(op.dateOperation).toLocaleDateString("fr-FR") : "—"}</td>
                    <td>
                      {op.libelle}
                      {op.referenceFactureExtraite && <span className="text-muted"> · réf {op.referenceFactureExtraite}</span>}
                    </td>
                    <td style={moneyCellStyle}>{euros(op.montantCents)}</td>
                    <td>{renderCategorieCell(op)}</td>
                    {showRapproch && <td>{renderPropositionCell(op)}</td>}
                    {showRapproch && <td style={{ minWidth: 220 }}>{renderFactureCell(op)}</td>}
                  </tr>
                ))}
                {filteredOps.length === 0 && isInitialized && (
                  <tr><td colSpan={nbCols} className="text-muted text-center py-3">
                    {operations.length === 0 ? "Aucun crédit dans ce relevé." : "Aucun crédit pour ces filtres."}
                  </td></tr>
                )}
              </tbody>
            </Table>
          )}
          {!isPipeline && filteredOps.length > visibleCount && (
            <div className="text-center my-2">
              <Button variant="outline-secondary" size="sm" onClick={() => setVisibleCount((c) => c + PAGE)}>
                Charger plus ({filteredOps.length - visibleCount} restant{filteredOps.length - visibleCount > 1 ? "s" : ""})
              </Button>
            </div>
          )}
          {!isPipeline && (importInfo || operations.length > 0) && (
            <div className="text-muted text-center" style={{ fontSize: "0.8rem" }}>
              {Math.min(visibleCount, filteredOps.length)} / {filteredOps.length} crédit(s) affiché(s)
            </div>
          )}
        </div>

        {stats && (
          <div className="d-flex flex-wrap mt-2" style={{ gap: 8 }}>
            <Badge variant="secondary">{stats.nbCredits} crédits</Badge>
            <Badge variant="success">{stats.nbRapproches} rapprochés</Badge>
            <Badge variant="info">{stats.nbEnvoyesSuivi} envoyés suivi</Badge>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default RapprochementBancaire;
